import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Plus, X } from "lucide-react";
import { addGame } from "@/lib/api";
import SectionHeading, { Reveal } from "./SectionHeading";

const HERO_KEYS = ["AVG", "OBP", "RBI", "SB"];
const HERO_COLORS = ["text-neon", "text-hotpink", "text-neonpurple", "text-white"];
const HERO_NOTES = {
  AVG: "Career Batting Average",
  OBP: "Career On-Base Pct",
  RBI: "Career Runs Batted In",
  SB: "Career Stolen Bases",
};

const field =
  "w-full border border-white/15 bg-black px-3 py-2.5 font-jbmono text-xs text-white outline-none transition-colors focus:border-neon";
const labelCls =
  "mb-1.5 block font-jbmono text-[10px] uppercase tracking-[0.25em] text-white/50";
const HOT_COLS = ["AVG", "OBP", "OPS"];
const PITCH_HOT = ["ERA", "WHIP", "SO"];

function CareerTable({ title, testid, stats, gcProfile, hotCols }) {
  const cols = stats.columns;
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-6 py-4">
        <span className="font-bebas text-2xl tracking-wide">
          {title}
        </span>
        <a
          data-testid={`${testid}-gc-link`}
          href={gcProfile}
          target="_blank"
          rel="noreferrer"
          className="group flex items-center gap-2 font-jbmono text-[10px] tracking-[0.25em] text-neon transition-colors hover:text-white"
        >
          LIVE ON GAMECHANGER
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>
      <div className="overflow-x-auto">
        <table data-testid={testid} className="w-full min-w-[900px]">
          <thead>
            <tr>
              <th className="border-b border-white/10 px-4 py-3 text-left font-jbmono text-[10px] font-normal tracking-[0.2em] text-white/40">
                SEASON
              </th>
              {cols.map((c) => (
                <th
                  key={c}
                  className="border-b border-white/10 px-3 py-3 text-right font-jbmono text-[10px] font-normal tracking-[0.15em] text-white/40"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stats.seasons.map((s, i) => (
              <tr
                key={i}
                data-testid={`${testid}-season-${i + 1}`}
                className="transition-colors hover:bg-white/[0.03]"
              >
                <td className="border-b border-white/5 px-4 py-4">
                  <p className="flex items-center gap-2 font-bebas text-lg leading-none tracking-wide">
                    {s.season}
                    {s.season.includes("2026") && (
                      <span className="bg-hotpink/15 px-2 py-0.5 font-jbmono text-[8px] tracking-[0.2em] text-hotpink">
                        CURRENT
                      </span>
                    )}
                  </p>
                  <p className="mt-1 font-jbmono text-[10px] text-white/40">
                    {s.team}
                  </p>
                </td>
                {cols.map((c) => (
                  <td
                    key={c}
                    className={`border-b border-white/5 px-3 py-4 text-right font-jbmono text-sm ${
                      hotCols.includes(c) ? "text-neon" : "text-white/80"
                    }`}
                  >
                    {s[c]}
                  </td>
                ))}
              </tr>
            ))}
            <tr data-testid={`${testid}-total-row`} className="bg-neon/5">
              <td className="px-4 py-4">
                <p className="font-bebas text-lg leading-none tracking-wide text-neon">
                  CAREER
                </p>
                <p className="mt-1 font-jbmono text-[10px] text-white/40">
                  {stats.career.team}
                </p>
              </td>
              {cols.map((c) => (
                <td
                  key={c}
                  className={`px-3 py-4 text-right font-jbmono text-sm font-semibold ${
                    hotCols.includes(c) ? "text-neon" : "text-white"
                  }`}
                >
                  {stats.career[c]}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GameLog({ games, upcomingSeason, onGamesChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const empty = {
    date: "",
    opponent: "",
    result: "",
    ab: "",
    h: "",
    hr: "",
    rbi: "",
    r: "",
    sb: "",
    passcode: "",
  };
  const [form, setForm] = useState(empty);

  const totals = games.reduce(
    (a, g) => ({
      ab: a.ab + g.ab,
      h: a.h + g.h,
      hr: a.hr + g.hr,
      rbi: a.rbi + g.rbi,
      r: a.r + g.r,
      sb: a.sb + g.sb,
    }),
    { ab: 0, h: 0, hr: 0, rbi: 0, r: 0, sb: 0 }
  );
  const avg =
    totals.ab > 0 ? (totals.h / totals.ab).toFixed(3).replace(/^0/, "") : "-";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await addGame({
        ...form,
        ab: +form.ab || 0,
        h: +form.h || 0,
        hr: +form.hr || 0,
        rbi: +form.rbi || 0,
        r: +form.r || 0,
        sb: +form.sb || 0,
      });
      toast.success("Game logged. The book grows.");
      setForm(empty);
      setOpen(false);
      onGamesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log game");
    } finally {
      setBusy(false);
    }
  };

  const numFields = [
    ["ab", "AB"],
    ["h", "H"],
    ["hr", "HR"],
    ["rbi", "RBI"],
    ["r", "R"],
    ["sb", "SB"],
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-6 py-4">
        <div>
          <span className="font-bebas text-2xl tracking-wide">
            {upcomingSeason.name} — GAME LOG
          </span>
          <p className="mt-1 font-jbmono text-[10px] uppercase tracking-[0.2em] text-white/40">
            {upcomingSeason.note}
          </p>
        </div>
        <button
          data-testid="game-add-toggle"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 rounded-full border border-neon/50 px-4 py-2 font-jbmono text-[10px] tracking-[0.25em] text-neon transition-colors hover:bg-neon hover:text-black"
        >
          {open ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {open ? "CLOSE" : "LOG A GAME"}
        </button>
      </div>

      {open && (
        <form
          data-testid="game-form"
          onSubmit={submit}
          className="grid grid-cols-2 gap-4 border-b border-white/10 p-6 md:grid-cols-6"
        >
          <div className="col-span-2">
            <label className={labelCls}>Date</label>
            <input
              data-testid="game-form-date"
              type="date"
              required
              className={field}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Opponent</label>
            <input
              data-testid="game-form-opponent"
              required
              className={field}
              placeholder="vs. Tacoma Thunder"
              value={form.opponent}
              onChange={(e) => setForm({ ...form, opponent: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Result</label>
            <input
              data-testid="game-form-result"
              className={field}
              placeholder="W 8-3"
              value={form.result}
              onChange={(e) => setForm({ ...form, result: e.target.value })}
            />
          </div>
          {numFields.map(([key, lab]) => (
            <div key={key} className="col-span-1">
              <label className={labelCls}>{lab}</label>
              <input
                data-testid={`game-form-${key}`}
                type="number"
                min="0"
                className={field}
                placeholder="0"
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </div>
          ))}
          <div className="col-span-2 md:col-span-3">
            <label className={labelCls}>Family passcode</label>
            <input
              data-testid="game-form-passcode"
              type="password"
              required
              className={field}
              value={form.passcode}
              onChange={(e) => setForm({ ...form, passcode: e.target.value })}
            />
          </div>
          <div className="col-span-2 flex items-end md:col-span-3">
            <button
              data-testid="game-form-submit"
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-neon py-3 font-bebas text-xl tracking-widest text-black transition-shadow hover:shadow-[0_0_25px_rgba(57,255,20,0.5)] disabled:opacity-50"
            >
              {busy ? "LOGGING..." : "LOG GAME"}
            </button>
          </div>
        </form>
      )}

      {games.length === 0 ? (
        <div data-testid="game-log-empty" className="px-6 py-14 text-center">
          <p className="font-bebas text-3xl tracking-wide text-white/60">
            NO GAMES YET
          </p>
          <p className="mx-auto mt-2 max-w-md font-jbmono text-xs leading-relaxed text-white/40">
            The {upcomingSeason.name} season hasn't started. Every game gets
            logged here — at-bats, hits, bombs, steals, all of it.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table data-testid="game-log-table" className="w-full min-w-[760px]">
            <thead>
              <tr>
                {["DATE", "OPPONENT", "RES", "AB", "H", "HR", "RBI", "R", "SB"].map(
                  (h) => (
                    <th
                      key={h}
                      className="border-b border-white/10 px-4 py-3 text-left font-jbmono text-[10px] font-normal tracking-[0.2em] text-white/40"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {games.map((g, i) => (
                <tr
                  key={g.id}
                  data-testid={`game-row-${i + 1}`}
                  className="transition-colors hover:bg-white/[0.03]"
                >
                  <td className="border-b border-white/5 px-4 py-3 font-jbmono text-xs text-white/50">
                    {new Date(`${g.date}T00:00:00`)
                      .toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                      .toUpperCase()}
                  </td>
                  <td className="border-b border-white/5 px-4 py-3 font-bebas text-lg tracking-wide">
                    {g.opponent}
                  </td>
                  <td className="border-b border-white/5 px-4 py-3">
                    <span
                      className={`font-jbmono text-xs font-semibold ${
                        g.result?.toUpperCase().startsWith("W")
                          ? "text-neon"
                          : "text-hotpink"
                      }`}
                    >
                      {g.result || "-"}
                    </span>
                  </td>
                  {[g.ab, g.h, g.hr, g.rbi, g.r, g.sb].map((v, j) => (
                    <td
                      key={j}
                      className="border-b border-white/5 px-4 py-3 font-jbmono text-sm text-white/80"
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
              <tr data-testid="game-log-totals" className="bg-neon/5">
                <td className="px-4 py-3 font-jbmono text-xs text-neon">
                  {games.length} GP
                </td>
                <td className="px-4 py-3 font-bebas text-lg tracking-wide text-neon">
                  12UP TOTALS
                </td>
                <td className="px-4 py-3 font-jbmono text-xs text-white/50">
                  AVG {avg}
                </td>
                {[totals.ab, totals.h, totals.hr, totals.rbi, totals.r, totals.sb].map(
                  (v, j) => (
                    <td
                      key={j}
                      className="px-4 py-3 font-jbmono text-sm font-semibold text-white"
                    >
                      {v}
                    </td>
                  )
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Scoreboard({
  careerStats,
  pitchingStats,
  gcProfile,
  upcomingSeason,
  games,
  onGamesChanged,
}) {
  const career = careerStats.career;

  return (
    <section
      id="scoreboard"
      data-testid="section-scoreboard"
      className="mx-auto max-w-7xl px-6 py-24 md:py-36"
    >
      <SectionHeading
        number="01"
        title="THE SCOREBOARD"
        sub="Career stats via GameChanger"
        id="scoreboard"
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {HERO_KEYS.map((k, i) => (
          <Reveal key={k} delay={i * 0.08} className={i === 0 ? "col-span-2" : ""}>
            <div
              data-testid={`stat-${k.toLowerCase()}`}
              className="group h-full rounded-3xl border border-white/10 bg-panel p-6 transition-colors duration-300 hover:border-neon md:p-8"
            >
              <p className="font-jbmono text-[10px] uppercase tracking-[0.3em] text-white/40">
                {HERO_NOTES[k]}
              </p>
              <p
                className={`mt-4 font-bebas leading-none transition-shadow duration-300 ${HERO_COLORS[i]} ${
                  i === 0 ? "text-8xl md:text-9xl" : "text-6xl md:text-7xl"
                } group-hover:drop-shadow-[0_0_20px_rgba(57,255,20,0.4)]`}
              >
                {career[k]}
              </p>
              <p className="mt-3 font-bebas text-xl tracking-widest text-white/50">
                {k}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-6 space-y-6">
        <Reveal>
          <CareerTable
            title="CAREER BATTING"
            testid="career-table"
            stats={careerStats}
            gcProfile={gcProfile}
            hotCols={HOT_COLS}
          />
        </Reveal>
        <Reveal delay={0.05}>
          <CareerTable
            title="CAREER PITCHING"
            testid="pitching-table"
            stats={pitchingStats}
            gcProfile={gcProfile}
            hotCols={PITCH_HOT}
          />
        </Reveal>
        <Reveal delay={0.08}>
          <GameLog
            games={games}
            upcomingSeason={upcomingSeason}
            onGamesChanged={onGamesChanged}
          />
        </Reveal>
      </div>
    </section>
  );
}
